import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowUpRight, ArrowLeft, MapPin, Clock, Layers, Wrench, CheckCircle2, MessageCircle } from "lucide-react";
import Seo, { buildBreadcrumbs } from "@/components/site/Seo";
import { CLIENT_PROJECTS, findProjectBySlug } from "@/data/projects";
import { AREAS } from "@/data/areas";
import { SERVICES } from "@/data/services";
import api from "@/lib/api";
import Lightbox from "@/components/site/Lightbox";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const resolveImg = (img) => (img && img.startsWith("/api/") ? `${BACKEND_URL}${img}` : img);

export default function ProjectDetail() {
  const { slug } = useParams();
  const [adminProject, setAdminProject] = useState(undefined); // undefined = loading
  const [lightboxIdx, setLightboxIdx] = useState(null);

  useEffect(() => {
    setAdminProject(undefined);
    api
      .get(`/projects/${slug}`)
      .then((r) => setAdminProject(r.data))
      .catch(() => setAdminProject(null));
  }, [slug]);

  // Prefer admin project, fall back to built-in
  const builtIn = useMemo(() => findProjectBySlug(slug), [slug]);
  const p = adminProject || builtIn;

  if (adminProject === undefined && !builtIn) {
    return <div className="max-w-3xl mx-auto px-4 py-32 text-slate-500">Loading…</div>;
  }
  if (!p) return <Navigate to="/projects" replace />;

  const area = p.area_slug && AREAS[p.area_slug] ? AREAS[p.area_slug] : null;
  const service = p.service_slug && SERVICES[p.service_slug] ? SERVICES[p.service_slug] : null;

  // Related built-in projects (same category, excluding current)
  const related = CLIENT_PROJECTS.filter(
    (r) => r.slug !== p.slug && (r.area_slug === p.area_slug || r.category === p.category)
  ).slice(0, 3);

  const gallery = Array.isArray(p.gallery) && p.gallery.length > 0 ? p.gallery : [];
  const hasGallery = gallery.length > 0;
  const beforeImgs = gallery.filter((g) => g.phase === "before");
  const duringImgs = gallery.filter((g) => g.phase === "during");
  const afterImgs = gallery.filter((g) => g.phase === "after");

  const heroImg = resolveImg(p.img);

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "CreativeWork",
      name: p.title,
      description: p.overview || p.description || p.blurb || `${p.category} project in ${p.location} by Prime Build Design LTD.`,
      image: heroImg,
      dateCreated: p.year,
      locationCreated: { "@type": "Place", name: p.location },
      creator: {
        "@type": "GeneralContractor",
        name: "Prime Build Design LTD",
        telephone: "+44 7481 626260",
        url: "https://primebuilddesign.co.uk",
      },
    },
    buildBreadcrumbs([
      { name: "Projects", path: "/projects" },
      { name: p.title, path: `/projects/${p.slug}` },
    ]),
  ];

  return (
    <div data-testid={`project-detail-${p.slug}`}>
      <Seo
        title={`${p.title} — ${p.category} in ${p.location}`}
        description={(p.overview || p.description || p.blurb || `${p.category} project in ${p.location}, delivered by Prime Build Design LTD.`).slice(0, 165)}
        path={`/projects/${p.slug}`}
        image={heroImg}
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="bg-slate-950 text-white">
        <div className="relative">
          <div className="absolute inset-0">
            <img src={heroImg} alt={p.title} className="w-full h-full object-cover opacity-40" loading="eager" decoding="async" />
            <div className="absolute inset-0 bg-gradient-to-b from-slate-950/80 via-slate-950/40 to-slate-950" />
          </div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
            <Link to="/projects" className="inline-flex items-center gap-1 text-sm text-slate-300 hover:text-white transition mb-8" data-testid="back-to-projects">
              <ArrowLeft size={14} /> All projects
            </Link>
            <div className="overline mb-6 text-[#C5A059] flex flex-wrap items-center gap-x-4 gap-y-1">
              <span className="inline-flex items-center gap-1"><MapPin size={12} /> {p.location}</span>
              <span>·</span>
              <span>{p.category}</span>
              <span>·</span>
              <span>{p.year}</span>
            </div>
            <h1 className="font-display text-4xl md:text-6xl font-bold tracking-tight leading-[1.05] max-w-4xl">
              {p.title}
            </h1>
            {(p.overview || p.description || p.blurb) && (
              <p className="mt-8 text-lg text-slate-300 max-w-3xl leading-relaxed">
                {p.overview || p.description || p.blurb}
              </p>
            )}
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/contact" className="btn-accent" data-testid={`project-${p.slug}-cta-quote`}>
                Request a similar quote <ArrowUpRight size={16} />
              </Link>
              {service && (
                <Link to={`/services/${service.slug}`} className="btn-ghost text-white border-white hover:bg-white hover:text-slate-900" data-testid={`project-${p.slug}-cta-service`}>
                  Learn about {service.name}
                </Link>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Meta bar */}
      <section className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 md:grid-cols-4 gap-px bg-slate-200">
          <MetaTile label="Location" value={p.location} icon={MapPin} />
          <MetaTile label="Service" value={service ? service.name : p.category} icon={Wrench} />
          <MetaTile label="Duration" value={p.duration_weeks ? `${p.duration_weeks} weeks` : p.year} icon={Clock} />
          <MetaTile label="Category" value={p.category} icon={Layers} />
        </div>
      </section>

      {/* Before / After / During gallery */}
      {hasGallery && (
        <section className="bg-white border-b border-slate-200" data-testid={`project-${p.slug}-gallery`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
            <div className="overline mb-3">Gallery</div>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-slate-900 tracking-tight mb-10">
              Before, during, after.
            </h2>
            {[
              { key: "before", label: "Before", items: beforeImgs },
              { key: "during", label: "During", items: duringImgs },
              { key: "after", label: "After", items: afterImgs },
            ].filter((g) => g.items.length > 0).map((group) => (
              <div key={group.key} className="mb-12">
                <div className="overline text-[#C5A059] mb-4">{group.label}</div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {group.items.map((g, i) => {
                    const flatIdx = gallery.indexOf(g);
                    return (
                      <button
                        key={i}
                        type="button"
                        onClick={() => setLightboxIdx(flatIdx)}
                        className="group relative aspect-[4/3] overflow-hidden bg-slate-100 cursor-zoom-in"
                        data-testid={`project-${p.slug}-gallery-${group.key}-${i}`}
                      >
                        <img src={resolveImg(g.url)} alt={g.caption || `${p.title} — ${group.label}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" decoding="async" />
                        {g.caption && (
                          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-slate-900/85 to-transparent p-4 text-white text-sm">
                            {g.caption}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Long-form content */}
      <section className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28 space-y-14">
          {p.overview && (
            <Block title="Project overview" body={p.overview} testId={`project-${p.slug}-overview`} />
          )}
          {Array.isArray(p.scope_of_works) && p.scope_of_works.length > 0 && (
            <div data-testid={`project-${p.slug}-scope`}>
              <div className="overline text-[#C5A059] mb-3">Scope of works</div>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-slate-900 tracking-tight mb-6">What we delivered</h2>
              <ul className="space-y-3">
                {p.scope_of_works.map((s, i) => (
                  <li key={i} className="flex gap-3 text-lg leading-relaxed text-slate-700">
                    <CheckCircle2 size={20} className="text-[#C5A059] flex-shrink-0 mt-1" /> {s}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {Array.isArray(p.materials_used) && p.materials_used.length > 0 && (
            <div data-testid={`project-${p.slug}-materials`}>
              <div className="overline text-[#C5A059] mb-3">Materials & specification</div>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-slate-900 tracking-tight mb-6">The materials</h2>
              <div className="flex flex-wrap gap-2">
                {p.materials_used.map((m, i) => (
                  <span key={i} className="border border-slate-300 px-4 py-2 text-sm text-slate-700 bg-slate-50">{m}</span>
                ))}
              </div>
            </div>
          )}
          {p.challenges_and_solutions && (
            <Block title="Challenges & solutions" body={p.challenges_and_solutions} testId={`project-${p.slug}-challenges`} />
          )}
        </div>
      </section>

      {/* Client testimonial */}
      {p.client_testimonial && (
        <section className="bg-slate-950 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
            <div className="font-display text-5xl text-[#C5A059]">&ldquo;</div>
            <p className="mt-4 font-display text-2xl md:text-3xl font-light max-w-3xl mx-auto leading-relaxed">
              {p.client_testimonial}
            </p>
            {p.client_testimonial_author && (
              <div className="mt-6 overline text-slate-400">{p.client_testimonial_author}</div>
            )}
          </div>
        </section>
      )}

      {/* Related services + area */}
      {(service || area) && (
        <section className="bg-slate-50 border-y border-slate-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
            <div className="overline mb-3">Related</div>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-slate-900 tracking-tight mb-10">
              Explore more.
            </h2>
            <div className="grid md:grid-cols-2 gap-px bg-slate-200">
              {service && (
                <Link
                  to={`/services/${service.slug}`}
                  data-testid={`project-${p.slug}-related-service`}
                  className="bg-white p-8 hover:bg-slate-100 transition-colors group"
                >
                  <div className="overline text-[#C5A059]">Service</div>
                  <div className="font-display text-2xl font-semibold text-slate-900 mt-3">{service.name}</div>
                  <div className="text-sm text-slate-600 mt-2 line-clamp-2">{service.intro}</div>
                  <div className="text-sm font-medium mt-5 inline-flex items-center gap-1 text-slate-900 group-hover:text-[#C5A059] transition-colors">
                    Learn more <ArrowUpRight size={14} />
                  </div>
                </Link>
              )}
              {area && (
                <Link
                  to={`/areas/${area.name.toLowerCase()}`}
                  data-testid={`project-${p.slug}-related-area`}
                  className="bg-white p-8 hover:bg-slate-100 transition-colors group"
                >
                  <div className="overline text-[#C5A059]">Area · {area.postcode}</div>
                  <div className="font-display text-2xl font-semibold text-slate-900 mt-3">Builders in {area.name}</div>
                  <div className="text-sm text-slate-600 mt-2">{area.positioning}</div>
                  <div className="text-sm font-medium mt-5 inline-flex items-center gap-1 text-slate-900 group-hover:text-[#C5A059] transition-colors">
                    Explore {area.name} <ArrowUpRight size={14} />
                  </div>
                </Link>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Similar / nearby projects */}
      {related.length > 0 && (
        <section className="bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
            <div className="overline mb-3">Similar projects</div>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-slate-900 tracking-tight mb-10">
              {area ? `More work near ${area.name}.` : "More recent projects."}
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to={`/projects/${r.slug}`}
                  data-testid={`project-${p.slug}-similar-${r.slug}`}
                  className="group relative h-[380px] overflow-hidden"
                >
                  <img src={resolveImg(r.img)} alt={r.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" decoding="async" />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/25 to-transparent" />
                  <div className="absolute bottom-5 left-5 right-5 text-white">
                    <div className="overline text-white/70">{r.category} · {r.location}</div>
                    <div className="font-display text-xl font-semibold mt-1">{r.title}</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="bg-[#C5A059]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 grid md:grid-cols-2 gap-10 items-center">
          <h2 className="font-display text-4xl md:text-5xl font-bold text-white tracking-tight leading-tight">
            Ready to start something similar?
          </h2>
          <div className="flex flex-col sm:flex-row gap-4 md:justify-end">
            <Link to="/contact" className="bg-slate-900 text-white px-6 py-4 font-medium inline-flex items-center gap-2 hover:bg-slate-800 transition" data-testid={`project-${p.slug}-bottom-quote`}>
              Request a free quote <ArrowUpRight size={18} />
            </Link>
            <a href={`https://wa.me/447481626260?text=${encodeURIComponent(`Hi Prime Build, I saw your ${p.title} project — I'd like to discuss something similar.`)}`} target="_blank" rel="noreferrer" className="bg-white text-slate-900 px-6 py-4 font-medium inline-flex items-center gap-2 hover:bg-slate-50 transition" data-testid={`project-${p.slug}-bottom-wa`}>
              <MessageCircle size={18} /> WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {lightboxIdx !== null && gallery[lightboxIdx] && (
        <Lightbox
          open={true}
          onClose={() => setLightboxIdx(null)}
          src={resolveImg(gallery[lightboxIdx].url)}
          alt={gallery[lightboxIdx].caption || p.title}
          caption={p.title}
          subcaption={gallery[lightboxIdx].caption || gallery[lightboxIdx].phase}
        />
      )}
    </div>
  );
}

function MetaTile({ label, value, icon: Icon }) {
  return (
    <div className="bg-white p-5 md:p-6">
      <div className="flex items-center gap-2 overline text-slate-500">
        <Icon size={14} className="text-[#C5A059]" /> {label}
      </div>
      <div className="mt-2 font-display text-lg md:text-xl font-semibold text-slate-900">{value}</div>
    </div>
  );
}

function Block({ title, body, testId }) {
  return (
    <div data-testid={testId}>
      <div className="overline text-[#C5A059] mb-3">{title}</div>
      <h2 className="font-display text-3xl md:text-4xl font-bold text-slate-900 tracking-tight mb-6">{title}</h2>
      <div className="text-lg leading-[1.8] text-slate-700 whitespace-pre-wrap">{body}</div>
    </div>
  );
}
